import { useEffect, useState } from 'react';
import { PollCountdown } from '../components/PollCountdown';
import { useLive } from '../lib/live';
import { useOverlaySeite } from '../lib/overlay';
import type { OverlayState, Poll, Team } from '../lib/types';

/**
 * Das Challenge-Overlay für OBS.
 *
 * Es zeigt nacheinander zwei Dinge, denn mehr passt nicht nebeneinander auf einen Stream:
 *
 * 1. **Während der Abstimmung** die Frage mit den nummerierten Antworten, den Balken und der
 *    Restzeit. Die Nummer steht groß voran – sie ist das, was die Zuschauer in den Chat tippen.
 * 2. **Danach** die gewonnene Challenge. Im Modus je Kanal eine je Team, denn dort hat jedes
 *    Publikum für seinen eigenen Streamer entschieden.
 *
 * Läuft keine Abstimmung, zeigt es **nichts** – kein leerer Kasten, keine Überschrift. Eine
 * Browserquelle, die dauerhaft etwas Leeres zeichnet, muss man in OBS von Hand ein- und
 * ausblenden; so blendet sie sich selbst ein.
 */
export function ChallengeOverlay({ overlayKey }: { overlayKey: string }) {
  useOverlaySeite();
  const live = useLive<OverlayState>({ overlayKey });

  if (live.fehler) return <div className="overlay-hint">{live.fehler}</div>;

  const poll = live.state?.poll;
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(timer);
  }, []);
  if (poll && !poll.offen && (!poll.closedAt || now >= Date.parse(poll.closedAt) + 10_000)) {
    return <div className="overlay challenge-overlay leer" />;
  }
  if (!poll) return <div className="overlay challenge-overlay leer" />;

  return (
    <div className="overlay challenge-overlay">
      {poll.offen ? (
        <Abstimmung poll={poll} />
      ) : (
        <Ergebnis poll={poll} teams={live.state?.teams ?? []} />
      )}
    </div>
  );
}

function Abstimmung({ poll }: { poll: Poll }) {
  return (
    <div className="poll-karte">
      <div className="poll-kopf">
        <span className="poll-marke">Abstimmung</span>
        <PollCountdown closesAt={poll.closesAt} />
      </div>

      <h2 className="poll-frage">{poll.question}</h2>

      <ul className="poll-antworten">
        {poll.antworten.map((antwort) => (
          <li key={antwort.id}>
            {/* Der Balken liegt hinter dem Text, nicht daneben: So bleibt die Antwort auch dann
                lesbar, wenn sie gerade führt und der Balken die ganze Breite einnimmt. */}
            <div className="poll-balken" style={{ width: `${Math.round(antwort.anteil * 100)}%` }} />
            <span className="poll-nummer">{antwort.nummer}</span>
            <span className="poll-label">{antwort.label}</span>
            <span className="poll-stimmen">{antwort.stimmen}</span>
          </li>
        ))}
      </ul>

      <p className="poll-fuß">
        {poll.stimmenGesamt === 0
          ? 'Schreib die Nummer in den Chat.'
          : `${poll.stimmenGesamt} ${poll.stimmenGesamt === 1 ? 'Stimme' : 'Stimmen'}`}
      </p>
    </div>
  );
}

function Ergebnis({ poll, teams }: { poll: Poll; teams: Team[] }) {
  if (poll.mode === 'PER_CHANNEL' && poll.jeKanal) {
    const mitGewinner = poll.jeKanal.filter((e) => e.gewinner);
    if (mitGewinner.length === 0) return <div className="challenge-overlay leer" />;

    return (
      <div className="poll-karte">
        <span className="poll-marke">Challenge</span>
        <ul className="challenge-liste">
          {mitGewinner.map((eintrag) => {
            const team = teams.find((t) => t.id === eintrag.teamId);
            return (
              <li key={eintrag.channelId}>
                <span className="challenge-team" style={{ background: team?.color ?? '#555' }}>
                  {team?.name ?? 'Kanal'}
                </span>
                <span className="challenge-text">{eintrag.gewinner!.label}</span>
              </li>
            );
          })}
        </ul>
      </div>
    );
  }

  const gewinner = poll.ergebnis?.gewinner;

  // Gleichstand wird nicht verschwiegen – sonst steht im Stream eine Challenge, die niemand
  // gewählt hat, und keiner weiß, woher sie kommt.
  if (!gewinner) {
    if (poll.ergebnis?.gleichstand) {
      return (
        <div className="poll-karte">
          <span className="poll-marke">Gleichstand</span>
          <p className="challenge-text">
            {poll.ergebnis.führende.map((a) => a.label).join('  ·  ')}
          </p>
        </div>
      );
    }
    return <div className="challenge-overlay leer" />;
  }

  return (
    <div className="poll-karte">
      <span className="poll-marke">Challenge</span>
      <p className="challenge-text groß">{gewinner.label}</p>
      <p className="poll-fuß">
        {gewinner.label} · {poll.ergebnis!.stimmenGesamt}{' '}
        {poll.ergebnis!.stimmenGesamt === 1 ? 'Stimme' : 'Stimmen'}
      </p>
    </div>
  );
}
